import { Search, Filter, Languages, UserCheck, DollarSign } from 'lucide-react';
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";

interface Category {
  id: string;
  name: string;
}

interface DirectorySearchProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  selectedCategory: string;
  onCategoryChange: (value: string) => void;
  categories: Category[];
  showCategoryFilter: boolean;
  // New filter props
  selectedLanguages: string[];
  onLanguagesChange: (languages: string[]) => void;
  selectedExperienceLevel: string;
  onExperienceChange: (level: string) => void;
  selectedAvailability: string;
  onAvailabilityChange: (status: string) => void;
  // New props for additional filters
  selectedLookingFor: string[];
  onLookingForChange: (categories: string[]) => void;
  budgetRange: [number, number];
  onBudgetRangeChange: (range: [number, number]) => void;
}

const lookingForOptions = [
  "Actor",
  "Director",
  "Producer",
  "Screenwriter",
  "Cinematographer",
  "Editor",
  "Sound Designer",
  "Costume Designer",
  "Makeup Artist",
  "Production Assistant"
];

export const DirectorySearch = ({
  searchTerm,
  onSearchChange,
  selectedCategory,
  onCategoryChange,
  categories,
  showCategoryFilter,
  selectedLanguages,
  onLanguagesChange,
  selectedExperienceLevel,
  onExperienceChange,
  selectedAvailability,
  onAvailabilityChange,
  selectedLookingFor,
  onLookingForChange,
  budgetRange,
  onBudgetRangeChange
}: DirectorySearchProps) => {
  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name or description..."
            className="pl-10"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>
        {showCategoryFilter && (
          <div className="w-full sm:w-64">
            <Select
              value={selectedCategory}
              onValueChange={onCategoryChange}
            >
              <SelectTrigger>
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Filter by category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((category) => (
                  <SelectItem key={category.id} value={category.id}>
                    {category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <Accordion type="single" collapsible className="w-full">
        <AccordionItem value="advanced-filters">
          <AccordionTrigger className="text-sm">Advanced Filters</AccordionTrigger>
          <AccordionContent>
            <div className="grid grid-cols-1 gap-4 pt-4">
              <div className="p-4 border rounded-lg space-y-4">
                <h3 className="font-medium flex items-center gap-2">
                  <UserCheck className="h-4 w-4" />
                  Looking For
                </h3>
                <div className="flex flex-wrap gap-2">
                  {lookingForOptions.map((option) => (
                    <Badge
                      key={option}
                      variant={selectedLookingFor.includes(option) ? "default" : "outline"}
                      className="cursor-pointer"
                      onClick={() => {
                        if (selectedLookingFor.includes(option)) {
                          onLookingForChange(selectedLookingFor.filter(item => item !== option));
                        } else {
                          onLookingForChange([...selectedLookingFor, option]);
                        }
                      }}
                    >
                      {option}
                    </Badge>
                  ))}
                </div>
              </div>

              <div className="p-4 border rounded-lg space-y-4">
                <h3 className="font-medium flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  Budget Range
                </h3>
                <Slider
                  defaultValue={[budgetRange[0], budgetRange[1]]}
                  max={10000}
                  step={100}
                  onValueChange={(value) => onBudgetRangeChange(value as [number, number])}
                  className="w-full"
                />
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>${budgetRange[0]}</span>
                  <span>${budgetRange[1]}</span>
                </div>
              </div>

              <div className="p-4 border rounded-lg space-y-4">
                <h3 className="font-medium flex items-center gap-2">
                  <Languages className="h-4 w-4" />
                  Languages
                </h3>
                <Select
                  value={selectedLanguages[0] || ''}
                  onValueChange={(value) => onLanguagesChange([value])}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Language" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">All Languages</SelectItem>
                    <SelectItem value="portuguese">Portuguese</SelectItem>
                    <SelectItem value="english">English</SelectItem>
                    <SelectItem value="spanish">Spanish</SelectItem>
                    <SelectItem value="french">French</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Select
                    value={selectedExperienceLevel}
                    onValueChange={onExperienceChange}
                  >
                    <SelectTrigger>
                      <UserCheck className="h-4 w-4 mr-2" />
                      <SelectValue placeholder="Experience Level" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Levels</SelectItem>
                      <SelectItem value="entry">Entry Level</SelectItem>
                      <SelectItem value="intermediate">Intermediate</SelectItem>
                      <SelectItem value="senior">Senior</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Select
                    value={selectedAvailability}
                    onValueChange={onAvailabilityChange}
                  >
                    <SelectTrigger>
                      <UserCheck className="h-4 w-4 mr-2" />
                      <SelectValue placeholder="Availability" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="available">Available</SelectItem>
                      <SelectItem value="busy">Busy</SelectItem>
                      <SelectItem value="not_available">Not Available</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};